/* global toastr */
import React, { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import GridLayout from 'react-grid-layout'
import { WidthProvider } from 'react-grid-layout'
import 'react-grid-layout/css/styles.css'
import { BaseView, baseStateToProps } from './smart_bps/lib/base_view'
import { request, wrapView } from './smart_bps/lib/utils'
import WidgetConstructorWizard from './widget_constructor/WidgetConstructorWizard'
import { DEFAULT_WIDGET_SIZE_BY_TYPE } from './widget_constructor/WidgetConstructorWizard'
import { AuthorizedDropDownMeny, UnauthorizedDropDownMenu } from './lib/NavDropdownMenu'
import './style.css'

const GridLayoutWithWidth = WidthProvider(GridLayout)

/** Минимальные ширина и высота виджетов в единицах сетки (cols × rows) */
const WIDGET_MIN_SIZES = {
  stats: { minW: 3, minH: 3 },
  bar_chart: { minW: 8, minH: 3 },
  donut_chart: { minW: 3, minH: 4 },
  my_requests: { minW: 4, minH: 4 },
}
const DEFAULT_MIN = { minW: 2, minH: 1 }

/** Виджеты, которые можно добавить на сводку (id и подпись для UI) */
const AVAILABLE_WIDGETS = [
  { id: 'stats', label: 'Счётчики (территории, заявки, режимы…)' },
  { id: 'bar_chart', label: 'Заявки за неделю/месяц' },
  { id: 'donut_chart', label: 'Заявки по статусам' },
  { id: 'my_requests', label: 'Мои заявки' },
]

/** Дефолтные размеры при добавлении виджета */
const DEFAULT_WIDGET_SIZE = {
  stats: { w: 12, h: 3 },
  bar_chart: { w: 8, h: 5 },
  donut_chart: { w: 6, h: 5 },
  my_requests: { w: 12, h: 4 },
}

function normalizeLayoutWithMins(layout) {
  return (layout || []).map((item) => {
    const mins = WIDGET_MIN_SIZES[item.i] || DEFAULT_MIN
    const minW = Math.max(item.minW ?? 0, mins.minW)
    const minH = Math.max(item.minH ?? 0, mins.minH)
    return {
      ...item,
      minW,
      minH,
      w: Math.max(item.w ?? 1, minW),
      h: Math.max(item.h ?? 1, minH),
    }
  })
}

/** Счётчик с анимацией от 0 до value за duration мс */
function CountUp({ value, duration = 900 }) {
  const [display, setDisplay] = useState(typeof value === 'number' ? 0 : value)
  const isFirstMount = useRef(true)

  useEffect(() => {
    if (typeof value !== 'number') {
      setDisplay(value)
      return
    }
    const start = isFirstMount.current ? 0 : display
    isFirstMount.current = false
    if (start === value) {
      setDisplay(value)
      return
    }
    const startTime = performance.now()
    const tick = (now) => {
      const elapsed = now - startTime
      const t = Math.min(elapsed / duration, 1)
      const eased = 1 - (1 - t) ** 2.5
      const current = Math.round(start + (value - start) * eased)
      setDisplay(current)
      if (t < 1) requestAnimationFrame(tick)
    }
    requestAnimationFrame(tick)
  }, [value, duration])

  return <span>{display}</span>
}

// Шаблон стат-карточек (порядок и метки)
const WIDGET_STATS_KEYS = [
  { key: 'territories', label: 'Территории', icon: 'ti-map-2' },
  { key: 'requests', label: 'Заявки', icon: 'ti-file-text' },
  { key: 'modes', label: 'Режимы', icon: 'ti-adjustments' },
  { key: 'access_objects', label: 'Люди', icon: 'ti-users' },
  { key: 'object_groups', label: 'Группы посетителей', icon: 'ti-folder' },
  { key: 'contractors', label: 'Контрагенты', icon: 'ti-building' },
  { key: 'granted_accesses', label: 'Согласованные доступы', icon: 'ti-key' },
]

/** Краткая дата из ISO: "13.02.2026" */
function formatShortDate(isoDate) {
  if (!isoDate) return ''
  const parts = String(isoDate).split('T')[0].split('-')
  if (parts.length < 3) return isoDate
  const [y, m, d] = parts
  return `${d}.${m}.${y}`
}

/** Дата и время из ISO для «Последняя синхронизация» в МСК: "13.02.2026 15:30" */
function formatShortDateTime(isoDate) {
  if (!isoDate) return ''
  const d = new Date(isoDate)
  if (Number.isNaN(d.getTime())) return String(isoDate)
  return d.toLocaleString('ru-RU', {
    timeZone: 'Europe/Moscow',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
}

/** Формат даты для подписи столбца: "07.02", "13.02" (день.месяц) */
function formatBarLabel(isoDate) {
  if (!isoDate) return ''
  const parts = String(isoDate).split('-')
  const day = parts[2] ? parseInt(parts[2], 10) : 0
  const month = parts[1] ? parseInt(parts[1], 10) : 0
  const d = day < 10 ? '0' + day : String(day)
  const m = month < 10 ? '0' + month : String(month)
  return `${d}.${m}`
}

// Цвета и подписи этапов заявок
const DONUT_COLORS = {
  draft: '#28c76f',
  validation: '#ff9f43',
  review: '#00cfe8',
  done: '#6c757d',
  rework: "#ff6B4a"
}
const REQUEST_STATE_LABELS = {
  draft: 'Создание',
  validation: 'Проверка',
  review: 'Согласование',
  done: 'Выполнено',
  rework: 'Доработка'
}

class IndexView extends BaseView {
  constructor(props) {
    super(props)
    this.auth_required = false
    this.state = {
      ...(this.state || {}),
      widgetStats: null,
      widgetBar: null,
      widgetBarDates: null,
      widgetDonut: null,
      widgetPeriod: 'week',
      widgetMyRequests: null,
      widgetEntitiesById: {},
      customWidgetData: {}, // item.i -> { value } | { items } для виджетов с config
      integrationEnabled: false,
      integrationLastSync: null,
      integrationSyncing: false,
      widgetLoading: true,
      barTooltip: null,
      dashboardLayout: normalizeLayoutWithMins([
        { i: 'stats', x: 0, y: 0, w: 12, h: 3 },
        { i: 'donut_chart', x: 0, y: 3, w: 4, h: 5 },
        { i: 'bar_chart', x: 4, y: 3, w: 8, h: 5 },
        { i: 'my_requests', x: 0, y: 8, w: 12, h: 4 },
      ]),
      editLayout: false,
      showWidgetConstructorModal: false,
      editWidgetId: null,
      editConfig: null,
    }
    this.loadDashboardLayout = this.loadDashboardLayout.bind(this)
    this.toggleEditLayout = this.toggleEditLayout.bind(this)
    this.saveDashboardLayout = this.saveDashboardLayout.bind(this)
    this.cancelEditLayout = this.cancelEditLayout.bind(this)
    this.onLayoutChange = this.onLayoutChange.bind(this)
    this.renderTopPanelRightItems = this.renderTopPanelRightItems.bind(this)
    this.loadCustomWidgetData = this.loadCustomWidgetData.bind(this)
    this.openWidgetConstructor = this.openWidgetConstructor.bind(this)
    this.openWidgetConstructorForEdit = this.openWidgetConstructorForEdit.bind(this)
    this.openWidgetConstructorDuplicate = this.openWidgetConstructorDuplicate.bind(this)
    this.closeWidgetConstructor = this.closeWidgetConstructor.bind(this)
    this.handleWidgetConstructorSave = this.handleWidgetConstructorSave.bind(this)
    this.widgetConstructorModalRef = React.createRef()
  }

  openWidgetConstructor() {
    this.setState({ showWidgetConstructorModal: true, editWidgetId: null, editConfig: null })
  }

  openWidgetConstructorForEdit(widgetId, config) {
    this.setState({ showWidgetConstructorModal: true, editWidgetId: widgetId, editConfig: config || null })
  }

  openWidgetConstructorDuplicate(config) {
    this.setState({ showWidgetConstructorModal: true, editWidgetId: null, editConfig: config || null })
  }

  closeWidgetConstructor() {
    this.setState({ showWidgetConstructorModal: false, editWidgetId: null, editConfig: null })
  }

  handleWidgetConstructorSave(config, widgetId, options, callback) {
    const { dashboardLayout } = this.state
    const done = typeof callback === 'function' ? callback : () => {}
    if (widgetId) {
      this.setState(
        (s) => ({
          dashboardLayout: normalizeLayoutWithMins(s.dashboardLayout.map((item) =>
            item.i === widgetId ? { ...item, config } : item
          )),
          showWidgetConstructorModal: false,
          editWidgetId: null,
          editConfig: null,
        }),
        () => {
          this.loadCustomWidgetData(config, widgetId)
          done()
        }
      )
    } else {
      const i = `custom_${Date.now()}`
      const maxY = dashboardLayout.length ? Math.max(...dashboardLayout.map((item) => item.y + item.h)) : 0
      const size = (options && options.size) || DEFAULT_WIDGET_SIZE_BY_TYPE[config.type] || { w: 6, h: 3 }
      const newItem = normalizeLayoutWithMins([
        { i, x: 0, y: maxY, w: size.w, h: size.h, config },
      ])[0]
      this.setState(
        (s) => ({ dashboardLayout: normalizeLayoutWithMins([...s.dashboardLayout, newItem]), showWidgetConstructorModal: false }),
        () => {
          this.loadCustomWidgetData(config, i)
          done()
        }
      )
    }
  }

  loadCustomWidgetData(config, widgetId) {
    if (!config || !config.entity || !config.type) return
    request({
      method: 'post',
      url: '/api/widgets/data',
      data: config,
      success: (data) => {
        if (data && !data.error) {
          this.setState((s) => ({ customWidgetData: { ...s.customWidgetData, [widgetId]: data } }))
        }
      },
      error: () => {
        this.setState((s) => ({ customWidgetData: { ...s.customWidgetData, [widgetId]: null } }))
      },
    })
  }

  loadDashboardLayout() {
    const defaultLayout = [
      { i: 'stats', x: 0, y: 0, w: 12, h: 3 },
      { i: 'donut_chart', x: 0, y: 3, w: 4, h: 5 },
      { i: 'bar_chart', x: 4, y: 3, w: 8, h: 5 },
      { i: 'my_requests', x: 0, y: 8, w: 12, h: 4 },
    ]
    request({
      method: 'get',
      url: '/api/widgets/layout',
      success: (data) => {
        const raw = Array.isArray(data.layout) && data.layout.length > 0 ? data.layout : defaultLayout
        const layout = raw.filter((item) => item.i !== 'quick_links' && item.i !== 'integration')
        const normalized = normalizeLayoutWithMins(layout.length > 0 ? layout : defaultLayout)
        this.setState({ dashboardLayout: normalized }, () => {
          normalized.filter((item) => item.config).forEach((item) => {
            this.loadCustomWidgetData(item.config, item.i)
          })
        })
      },
      error: () => {
        this.setState({ dashboardLayout: normalizeLayoutWithMins(defaultLayout) })
      },
    })
  }

  toggleEditLayout() {
    this.setState((s) => ({ editLayout: !s.editLayout }))
  }

  saveDashboardLayout() {
    const layout = this.state.dashboardLayout.filter((item) => item.i !== 'quick_links')
    request({
      method: 'post',
      url: '/api/widgets/layout',
      data: { layout },
      success: () => {
        this.setState({ editLayout: false })
        if (typeof toastr !== 'undefined') toastr.success('Раскладка виджетов сохранена')
      },
      error: (err) => {
        if (typeof toastr !== 'undefined') toastr.error(err.detail || 'Ошибка сохранения')
      },
    })
  }

  cancelEditLayout() {
    this.loadDashboardLayout()
    this.setState({ editLayout: false })
  }

  onLayoutChange(newLayout) {
    if (this.state.editLayout && Array.isArray(newLayout)) {
      const prev = this.state.dashboardLayout
      const merged = newLayout.map((item) => {
        const existing = prev.find((p) => p.i === item.i)
        return {
          ...item,
          ...(existing && existing.config != null && { config: existing.config }),
          ...(existing && existing.minW != null && { minW: existing.minW }),
          ...(existing && existing.minH != null && { minH: existing.minH }),
        }
      })
      this.setState({ dashboardLayout: normalizeLayoutWithMins(merged) })
    }
  }

  removeWidget(id) {
    this.setState((s) => ({
      dashboardLayout: normalizeLayoutWithMins(s.dashboardLayout.filter((item) => item.i !== id)),
    }))
  }

  addWidget(id) {
    const { dashboardLayout } = this.state
    if (dashboardLayout.some((item) => item.i === id)) return
    const maxY = dashboardLayout.length
      ? Math.max(...dashboardLayout.map((item) => item.y + item.h))
      : 0
    const size = DEFAULT_WIDGET_SIZE[id] || { w: 6, h: 3 }
    const newItem = normalizeLayoutWithMins([
      { i: id, x: 0, y: maxY, w: size.w, h: size.h },
    ])[0]
    this.setState((s) => ({
      dashboardLayout: normalizeLayoutWithMins([...s.dashboardLayout, newItem]),
    }))
  }

  renderWidget(item, data) {
    const key = typeof item === 'string' ? item : item.i
    const config = typeof item === 'object' && item ? item.config : null
    const customData = config ? this.state.customWidgetData[key] : null
    const { statsList, barData, barLabels, donutList, donutTotal, maxBar, BAR_HEIGHT_PX } = data

    if (config && (config.type === 'counter' || config.type === 'list' || config.type === 'bar' || config.type === 'donut')) {
      const title =
        config.title ||
        (config.entity
          ? this.state.widgetEntitiesById[config.entity] ||
            (config.entity === 'requests' ? 'Заявки' : config.entity)
          : 'Виджет')
      if (config.type === 'counter') {
        const value = customData && typeof customData.value === 'number' ? customData.value : '—'
        return (
          <div className="sigur-widgets__stats">
            <div className="sigur-stat">
              <div className="sigur-stat__head">
                <div className="sigur-stat__icon">
                  <i className="ti ti-chart-bar" />
                </div>
                <span className="sigur-stat__value">
                  {typeof value === 'number' ? <CountUp value={value} duration={900} /> : value}
                </span>
              </div>
              <span className="sigur-stat__label">{title}</span>
            </div>
          </div>
        )
      }
      if (config.type === 'list') {
        const items = Array.isArray(customData?.items) ? customData.items : []
        const isEdit = this.state.editLayout
        const isRequests = config.entity === 'requests'
        const listUrl = `/${config.entity || 'requests'}`
        return (
          <div className={`sigur-my-requests ${isEdit ? 'sigur-my-requests--no-pointer' : ''}`}>
            <h3 className="sigur-chart__title mb-3">{title}</h3>
            {items.length > 0 ? (
              <>
                <div className="sigur-my-requests__list">
                  {items.map((item) => {
                    if (isRequests) {
                      const metaParts = []
                      if (item.d_last_transition) metaParts.push(`Переход: ${formatShortDate(item.d_last_transition)}`)
                      if (item.visit_purpose) metaParts.push(`Цель: ${item.visit_purpose}`)
                      if (item.territory) metaParts.push(`Территория: ${item.territory}`)
                      return (
                        <Link
                          key={item.id}
                          to={`/requests/${item.id}/`}
                          className="sigur-my-requests__card"
                        >
                          <i className="ti ti-file-text sigur-my-requests__icon" aria-hidden />
                          <div className="sigur-my-requests__body">
                            <div className="sigur-my-requests__row">
                              <span className="sigur-my-requests__title">Заявка #{item.id}</span>
                              <span
                                className="sigur-my-requests__badge"
                                style={{ backgroundColor: DONUT_COLORS[item.state] || '#6c757d' }}
                              >
                                {REQUEST_STATE_LABELS[item.state] || item.state}
                              </span>
                            </div>
                            {metaParts.length > 0 && (
                              <span className="sigur-my-requests__meta">{metaParts.join(' · ')}</span>
                            )}
                          </div>
                          <i className="ti ti-chevron-right sigur-my-requests__arrow" aria-hidden />
                        </Link>
                      )
                    }
                    const itemTitle = item.title != null ? item.title : `#${item.id}`
                    return (
                      <Link
                        key={item.id}
                        to={`${listUrl}/${item.id}/`}
                        className="sigur-my-requests__card"
                      >
                        <i className="ti ti-file-text sigur-my-requests__icon" aria-hidden />
                        <div className="sigur-my-requests__body">
                          <div className="sigur-my-requests__row">
                            <span className="sigur-my-requests__title">{itemTitle}</span>
                          </div>
                        </div>
                        <i className="ti ti-chevron-right sigur-my-requests__arrow" aria-hidden />
                      </Link>
                    )
                  })}
                </div>
                <Link to={listUrl} className="sigur-my-requests__footer">
                  {isRequests ? 'Все заявки' : 'Все записи'}
                </Link>
              </>
            ) : (
              <p className="text-muted small mb-0">Нет записей</p>
            )}
          </div>
        )
      }
      if (config.type === 'bar') {
        const labels = Array.isArray(customData?.labels) ? customData.labels : []
        const values = Array.isArray(customData?.values) ? customData.values : []
        const maxVal = values.length ? Math.max(...values, 1) : 1
        const title = config.title || 'Гистограмма'
        const formatLabel = (isoDate) => {
          if (!isoDate) return ''
          const parts = String(isoDate).split('T')[0].split('-')
          return parts.length >= 3 ? `${parts[2]}.${parts[1]}` : isoDate
        }
        const isMonth = values.length > 14
        return (
          <div className="sigur-chart sigur-chart--bar">
            <h3 className="sigur-chart__title mb-2">{title}</h3>
            <div className="sigur-chart__bar-wrap">
              {values.map((val, i) => {
                const barPercent = (val / maxVal) * 100
                const showLabel = !isMonth || i % 5 === 0 || i === values.length - 1
                const label = showLabel ? (labels[i] ? formatLabel(labels[i]) : labels[i] || String(i + 1)) : ''
                const tooltipText = labels[i] ? `${labels[i]} — ${val}` : `${val}`
                return (
                  <div
                    key={i}
                    className="sigur-chart__bar-item"
                    onMouseEnter={(e) => this.setState({ barTooltip: { text: tooltipText, x: e.clientX, y: e.clientY } })}
                    onMouseLeave={() => this.setState({ barTooltip: null })}
                    onMouseMove={(e) => this.setState((s) => s.barTooltip ? { barTooltip: { ...s.barTooltip, x: e.clientX, y: e.clientY } } : null)}
                  >
                    <div className="sigur-chart__bar-track">
                      <div className="sigur-chart__bar-fill" style={{ height: Math.max(2, barPercent) + '%' }} />
                    </div>
                    <span className="sigur-chart__bar-label">{label}</span>
                  </div>
                )
              })}
            </div>
          </div>
        )
      }
      if (config.type === 'donut') {
        const items = Array.isArray(customData?.items) ? customData.items : []
        const total = customData?.total ?? 0
        const title = config.title || 'Распределение'
        return (
          <div className="sigur-chart sigur-chart--donut">
            <h3 className="sigur-chart__title">{title}</h3>
            <div className="sigur-chart__donut-wrap">
              <div
                className="sigur-chart__donut"
                style={{
                  background: items.length
                    ? `conic-gradient(${items.map((s, i) => {
                        const prev = items.slice(0, i).reduce((a, x) => a + x.percent, 0)
                        return `${s.color || '#6c757d'} ${prev}% ${prev + s.percent}%`
                      }).join(', ')})`
                    : 'conic-gradient(#e9ecef 0% 100%)',
                }}
              >
                <div className="sigur-chart__donut-hole">
                  <span className="sigur-chart__donut-total">{total}</span>
                  <span className="sigur-chart__donut-sublabel">всего</span>
                </div>
              </div>
            </div>
            <div className="sigur-chart__legend">
              {items.length
                ? items.map((s) => (
                    <div key={s.label} className="sigur-chart__legend-item">
                      <span className="sigur-chart__legend-dot" style={{ backgroundColor: s.color || '#6c757d' }} />
                      <span>{s.label} — {s.percent}%</span>
                    </div>
                  ))
                : <span className="sigur-chart__legend-empty">Нет данных</span>}
            </div>
          </div>
        )
      }
    }

    if (key === 'stats') {
      return (
        <div className="sigur-widgets__stats">
          {(statsList || []).map((stat) => (
            <div key={stat.label} className="sigur-stat">
              <div className="sigur-stat__head">
                <div className="sigur-stat__icon">
                  <i className={`ti ${stat.icon}`} />
                </div>
                <span className="sigur-stat__value">
                  {typeof stat.value === 'number' ? (
                    <CountUp value={stat.value} duration={900} />
                  ) : (
                    stat.value
                  )}
                </span>
              </div>
              <span className="sigur-stat__label">{stat.label}</span>
            </div>
          ))}
        </div>
      )
    }
    if (key === 'bar_chart') {
      const isMonth = this.state.widgetPeriod === 'month'
      return (
        <div className={`sigur-chart sigur-chart--bar${isMonth ? ' sigur-chart--bar--month' : ''}`}>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
            <h3 className="sigur-chart__title mb-0">
              Заявки {isMonth ? 'за месяц' : 'за неделю'}
            </h3>
            <div className="btn-group btn-group-sm">
              <button
                type="button"
                className={`btn ${this.state.widgetPeriod === 'week' ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => { this.setState({ widgetPeriod: 'week' }); this.loadWidgets('week') }}
              >
                НЕДЕЛЯ
              </button>
              <button
                type="button"
                className={`btn ${this.state.widgetPeriod === 'month' ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => { this.setState({ widgetPeriod: 'month' }); this.loadWidgets('month') }}
              >
                МЕСЯЦ
              </button>
            </div>
          </div>
          <div className="sigur-chart__bar-wrap">
            {(barData || []).map((val, i) => {
              const barPercent = (maxBar && maxBar > 0) ? Math.max(2, (val / maxBar) * 100) : 0
              const showLabel = !isMonth || i % 5 === 0 || i === (barData || []).length - 1
              const label = showLabel ? (barLabels && barLabels[i]) || String(i + 1) : ''
              const tooltipText = (barLabels && barLabels[i]) ? `${barLabels[i]} — ${val} заявок` : `${val} заявок`
              return (
                <div
                  key={i}
                  className="sigur-chart__bar-item"
                  onMouseEnter={(e) => this.setState({ barTooltip: { text: tooltipText, x: e.clientX, y: e.clientY } })}
                  onMouseLeave={() => this.setState({ barTooltip: null })}
                  onMouseMove={(e) => this.setState((s) => s.barTooltip ? { barTooltip: { ...s.barTooltip, x: e.clientX, y: e.clientY } } : null)}
                >
                  <div className="sigur-chart__bar-track">
                    <div className="sigur-chart__bar-fill" style={{ height: barPercent + '%' }} />
                  </div>
                  <span className="sigur-chart__bar-label">{label}</span>
                </div>
              )
            })}
          </div>
        </div>
      )
    }
    if (key === 'donut_chart') {
      return (
        <div className="sigur-chart sigur-chart--donut">
          <h3 className="sigur-chart__title">Заявки по статусам</h3>
          <div className="sigur-chart__donut-wrap">
            <div
              className="sigur-chart__donut"
              style={{
                background: (donutList || []).length
                  ? `conic-gradient(${(donutList || []).map((s, i) => {
                      const prev = (donutList || []).slice(0, i).reduce((a, x) => a + x.value, 0)
                      return `${s.color} ${prev}% ${prev + s.value}%`
                    }).join(', ')})`
                  : 'conic-gradient(#e9ecef 0% 100%)',
              }}
            >
              <div className="sigur-chart__donut-hole">
                <span className="sigur-chart__donut-total">{donutTotal || 0}</span>
                <span className="sigur-chart__donut-sublabel">всего</span>
              </div>
            </div>
          </div>
          <div className="sigur-chart__legend">
            {(donutList || []).length
              ? (donutList || []).map((s) => (
                  <div key={s.label} className="sigur-chart__legend-item">
                    <span className="sigur-chart__legend-dot" style={{ backgroundColor: s.color }} />
                    <span>{s.label} — {s.value}%</span>
                  </div>
                ))
              : <span className="sigur-chart__legend-empty">Нет заявок</span>}
          </div>
        </div>
      )
    }
    if (key === 'my_requests') {
      const items = Array.isArray(this.state.widgetMyRequests) ? this.state.widgetMyRequests : []
      const isEdit = this.state.editLayout
      return (
        <div className={`sigur-my-requests ${isEdit ? 'sigur-my-requests--no-pointer' : ''}`}>
          <h3 className="sigur-chart__title mb-3">Мои заявки</h3>
          {items.length > 0 ? (
            <>
              <div className="sigur-my-requests__list">
                {items.map((item) => {
                  const metaParts = []
                  if (item.d_last_transition) metaParts.push(`Переход: ${formatShortDate(item.d_last_transition)}`)
                  if (item.visit_purpose) metaParts.push(`Цель: ${item.visit_purpose}`)
                  if (item.territory) metaParts.push(`Территория: ${item.territory}`)
                  return (
                    <Link
                      key={item.id}
                      to={`/requests/${item.id}/`}
                      className="sigur-my-requests__card"
                    >
                      <i className="ti ti-file-text sigur-my-requests__icon" aria-hidden />
                      <div className="sigur-my-requests__body">
                        <div className="sigur-my-requests__row">
                          <span className="sigur-my-requests__title">Заявка #{item.id}</span>
                          <span
                            className="sigur-my-requests__badge"
                            style={{ backgroundColor: DONUT_COLORS[item.state] || '#6c757d' }}
                          >
                            {REQUEST_STATE_LABELS[item.state] || item.state}
                          </span>
                        </div>
                        {metaParts.length > 0 && (
                          <span className="sigur-my-requests__meta">{metaParts.join(' · ')}</span>
                        )}
                      </div>
                      <i className="ti ti-chevron-right sigur-my-requests__arrow" aria-hidden />
                    </Link>
                  )
                })}
              </div>
              <Link to="/requests" className="sigur-my-requests__footer">
                Все заявки
              </Link>
            </>
          ) : (
            <p className="text-muted small mb-0">Нет заявок</p>
          )}
        </div>
      )
    }
    if (key === 'integration') {
      if (!this.state.integrationEnabled) {
        return (
          <div className="sigur-chart sigur-chart--integration">
            <h3 className="sigur-chart__title mb-2">Интеграция СКУД</h3>
            <p className="text-muted small mb-0">Интеграция отключена</p>
          </div>
        )
      }
      return (
        <div className="sigur-widgets__integration sigur-chart sigur-chart--integration">
          <h3 className="sigur-chart__title mb-2">Интеграция СКУД</h3>
          <p className="text-muted small mb-2">Синхронизация режимов с системой согласования заявок на доступ</p>
          {this.state.integrationLastSync && (
            <div className="sigur-integration-last mb-2">
              <div className="small fw-500 text-body">Последняя синхронизация</div>
              <div className="small text-muted">
                {formatShortDateTime(this.state.integrationLastSync.at) || this.state.integrationLastSync.at}
                {this.state.integrationLastSync.status === 'ok' && (
                  <span className="ms-2">
                    · новых: {this.state.integrationLastSync.new_in_skud ?? this.state.integrationLastSync.created ?? 0}
                    , изменено: {this.state.integrationLastSync.updated ?? 0}
                    , нет в СКУД: {this.state.integrationLastSync.removed_from_skud ?? this.state.integrationLastSync.deactivated ?? 0}
                    {(this.state.integrationLastSync.archived_by_rules ?? 0) > 0 && (
                      <>, по срокам: {this.state.integrationLastSync.archived_by_rules}</>
                    )}
                  </span>
                )}
                {this.state.integrationLastSync.status === 'skipped' && (
                  <span className="ms-2 text-warning">· пропущена</span>
                )}
              </div>
            </div>
          )}
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            disabled={this.state.integrationSyncing}
            onClick={() => this.syncIntegration()}
          >
            {this.state.integrationSyncing ? 'ЗАПУСК…' : 'СИНХРОНИЗИРОВАТЬ'}
          </button>
        </div>
      )
    }
    if (key.startsWith('custom_')) {
      return (
        <div className="sigur-chart sigur-chart--donut text-muted small p-3">
          <h3 className="sigur-chart__title mb-2">Кастомный виджет</h3>
          <p className="mb-0">Настройки виджета не сохранены. Удалите этот блок в режиме «Настроить виджеты» и создайте виджет заново.</p>
        </div>
      )
    }
    return (
      <div className="sigur-chart text-muted small p-2">
        Виджет «{key}» не найден
      </div>
    )
  }

  loadWidgets(period) {
    const p = period || this.state.widgetPeriod || 'week'
    request({
      method: 'get',
      url: `/api/widgets/fixed?period=${p}`,
      success: (data) => {
        this.setState({
          widgetStats: data.stats,
          widgetBar: data.bar,
          widgetBarDates: data.bar_dates || null,
          widgetDonut: data.donut,
          widgetPeriod: data.period || p,
          widgetLoading: false,
        })
      },
      error: () => {
        this.setState({ widgetLoading: false })
      },
    })
  }

  loadWidgetEntitiesTitles() {
    request({
      method: 'get',
      url: '/api/widgets/entities',
      success: (data) => {
        const entities = Array.isArray(data.entities) ? data.entities : []
        const map = {}
        entities.forEach((e) => {
          if (e && e.id) map[e.id] = e.title
        })
        this.setState({ widgetEntitiesById: map })
      },
      error: () => this.setState({ widgetEntitiesById: {} }),
    })
  }

  loadMyRequests() {
    request({
      method: 'get',
      url: '/api/widgets/my_requests?limit=7',
      success: (data) => this.setState({ widgetMyRequests: data.items || [] }),
      error: () => this.setState({ widgetMyRequests: [] }),
    })
  }

  measureBarTooltip(el) {
    if (!el || !this.state.barTooltip) return
    if (this.state.barTooltip.left !== undefined) return
    const pad = 8
    const r = el.getBoundingClientRect()
    if (r.right <= window.innerWidth - pad) return
    const x = this.state.barTooltip.x
    const left = Math.max(pad, x - 12 - el.offsetWidth)
    this.setState((s) => ({ barTooltip: { ...s.barTooltip, left } }))
  }

  loadIntegrationStatus() {
    request({
      method: 'get',
      url: '/api/integrations/sigur/status',
      success: (data) =>
        this.setState({
          integrationEnabled: !!data.enabled,
          integrationLastSync: data.last_sync || null,
        }),
      error: () => this.setState({ integrationEnabled: false, integrationLastSync: null }),
    })
  }

  syncIntegration() {
    if (this.state.integrationSyncing) return
    this.setState({ integrationSyncing: true })
    request({
      method: 'post',
      url: '/api/integrations/sigur/sync_modes',
      success: (data) => {
        const lastSync = {
          at: new Date().toISOString(),
          ...data,
        }
        this.setState({ integrationSyncing: false, integrationLastSync: lastSync })
        if (typeof toastr !== 'undefined') {
          if (data.status === 'skipped') {
            toastr.info(`Синхронизация пропущена: ${data.reason || ''}`)
          } else {
            const parts = []
            const newCount = data.new_in_skud ?? data.created
            if (newCount != null) parts.push(`новых: ${newCount}`)
            if (data.updated != null) parts.push(`изменено: ${data.updated}`)
            const removed = data.removed_from_skud ?? data.deactivated
            if (removed != null) parts.push(`нет в СКУД: ${removed}`)
            if (data.archived_by_rules != null && data.archived_by_rules > 0) {
              parts.push(`по срокам: ${data.archived_by_rules}`)
            }
            toastr.success(parts.length ? `Синхронизация выполнена. ${parts.join(', ')}` : 'Синхронизация выполнена')
          }
        }
      },
      error: () => this.setState({ integrationSyncing: false }),
    })
  }

  componentDidMount() {
    if (super.componentDidMount) super.componentDidMount()
    if (!this.authorized) return
    this.loadDashboardLayout()
    this.loadWidgetEntitiesTitles()
    this.loadWidgets('week')
    this.loadMyRequests()
    this.loadIntegrationStatus()
  }

  renderContent() {
    this.waitRenderReadyLock = false

    if (!this.authorized) {
      return (
        <div className="sigur-hero sigur-hero--guest">
          <div className="sigur-hero__accent" />
          <div className="sigur-hero__inner">
            <h1 className="sigur-hero__title">
              <span className="sigur-hero__brand">Sigur</span>
            </h1>
            <p className="sigur-hero__tagline">
              Система согласования заявок на доступ
            </p>
            <p className="sigur-hero__lead">
              Единая среда для управления отношениями с контрагентами путем согласования заявок на доступ к объектам инфраструктуры
            </p>
            <Link to="/auth/login" className="btn btn-primary btn-lg sigur-hero__cta">
              ВОЙТИ В СИСТЕМУ
            </Link>
            <Link to="/auth/contractor_registration" className="btn btn-primary btn-lg sigur-hero__cta ms-4">
              ЗАЯВКА НА РЕГИСТРАЦИЮ
            </Link>
          </div>
        </div>
      )
    }

    const name = this.props.user?.fio || this.props.user?.username || this.user_title || 'Пользователь'
    const stats = this.state.widgetStats
    const roles = new Set(this.props.user?.roles || [])
    const privileged = roles.has('admin') || roles.has('approver')
    const visibleStatsKeys = privileged
      ? WIDGET_STATS_KEYS
      : WIDGET_STATS_KEYS.filter(({ key }) => ['requests', 'access_objects', 'granted_accesses', 'object_groups'].includes(key))

    const statsList = visibleStatsKeys.map(({ key, label, icon }) => ({
      label,
      icon,
      value: stats && typeof stats[key] === 'number' ? stats[key] : '—',
    }))
    const BAR_DEMO = [0, 0, 0, 0, 0, 0, 0]
    const rawBar = Array.isArray(this.state.widgetBar) && this.state.widgetBar.length > 0 ? this.state.widgetBar : null
    const rawBarDates = Array.isArray(this.state.widgetBarDates) && this.state.widgetBarDates.length > 0 ? this.state.widgetBarDates : null
    const barData = (rawBar && rawBar.some((v) => v > 0)) ? rawBar : (this.state.widgetPeriod === 'month' ? Array(30).fill(0) : BAR_DEMO)
    const barDays = barData.length
    const maxBar = Math.max(...barData, 1)
    const BAR_HEIGHT_PX = 80
    const barLabels = rawBarDates && rawBarDates.length === barData.length
      ? rawBarDates.map(formatBarLabel)
      : barData.map((_, i) => String(i + 1))
    const donutList = (Array.isArray(this.state.widgetDonut) && this.state.widgetDonut.length > 0)
      ? this.state.widgetDonut.map((d) => ({
          label: d.label,
          value: d.percent,
          color: DONUT_COLORS[d.state] || '#6c757d',
        }))
      : []
    const donutTotal = (this.state.widgetDonut || []).reduce((s, d) => s + (d.count || 0), 0)
    return (
      <div className="sigur-hero sigur-hero--dashboard">
        <div className="sigur-hero__accent" />
        <div className="d-flex justify-content-between align-items-center px-3 py-2">
          <div className="sigur-hero__inner">
            <p className="sigur-hero__welcome">Добро пожаловать</p>
            <h1 className="sigur-hero__title sigur-hero__title--sm">
              <span className="sigur-hero__brand">Sigur</span>
            </h1>
            <p className="sigur-hero__user">{name}</p>
          </div>
        </div>

        <div className="row g-3 mt-3 sigur-dashboard-row">
          <div className="col-12 sigur-dashboard-headers">
            <div className={`sigur-widgets__header ${this.state.editLayout ? 'sigur-widgets__header--edit' : ''}`}>
              <h2 className="sigur-hero__quick-title mb-0">Сводка</h2>
              {this.state.editLayout ? (
                <div className="d-flex flex-wrap align-items-center gap-2">
                  <button type="button" className="btn btn-sm btn-outline-primary" onClick={this.openWidgetConstructor}>
                    СОЗДАТЬ ВИДЖЕТ
                  </button>
                  <div className="dropdown">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary dropdown-toggle"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                    >
                      ДОБАВИТЬ ВИДЖЕТ
                    </button>
                    <ul className="dropdown-menu dropdown-menu-end">
                      {AVAILABLE_WIDGETS.filter(
                        (w) => !this.state.dashboardLayout.some((item) => item.i === w.id)
                      ).map((w) => (
                        <li key={w.id}>
                          <button type="button" className="dropdown-item" onClick={() => this.addWidget(w.id)}>
                            {w.label}
                          </button>
                        </li>
                      ))}
                      {AVAILABLE_WIDGETS.filter(
                        (w) => !this.state.dashboardLayout.some((item) => item.i === w.id)
                      ).length === 0 && (
                        <li><span className="dropdown-item text-muted">Все виджеты добавлены</span></li>
                      )}
                    </ul>
                  </div>
                  <button type="button" className="btn btn-sm btn-primary" onClick={this.saveDashboardLayout}>
                    СОХРАНИТЬ
                  </button>
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={this.cancelEditLayout}>
                    ОТМЕНА
                  </button>
                </div>
              ) : (
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={this.toggleEditLayout}>
                  НАСТРОИТЬ ВИДЖЕТЫ
                </button>
              )}
            </div>
          </div>

          <div className="col-12">
            <div className="sigur-widgets">
              <div className={`sigur-dashboard-grid ${this.state.editLayout ? 'sigur-dashboard-grid--edit' : ''}`}>
                <GridLayoutWithWidth
                  className="layout sigur-dashboard-layout"
                  layout={this.state.dashboardLayout}
                  onLayoutChange={this.onLayoutChange}
                  cols={12}
                  rowHeight={80}
                  margin={[0, 0]}
                  isDraggable={this.state.editLayout}
                  isResizable={this.state.editLayout}
                  compactType="vertical"
                  preventCollision={false}
                >
                  {this.state.dashboardLayout.map((item) => (
                    <div key={item.i} className="sigur-dashboard-widget">
                      {this.state.editLayout && (
                        <>
                          {item.config && (
                            <>
                              <button
                                type="button"
                                className="sigur-dashboard-widget__configure"
                                onClick={(e) => { e.stopPropagation(); this.openWidgetConstructorForEdit(item.i, item.config) }}
                                onMouseDown={(e) => e.stopPropagation()}
                                onPointerDown={(e) => e.stopPropagation()}
                                aria-label="Настроить виджет"
                              >
                                <i className="ti ti-settings" aria-hidden />
                              </button>
                              <button
                                type="button"
                                className="sigur-dashboard-widget__duplicate"
                                onClick={(e) => { e.stopPropagation(); this.openWidgetConstructorDuplicate(item.config) }}
                                onMouseDown={(e) => e.stopPropagation()}
                                onPointerDown={(e) => e.stopPropagation()}
                                aria-label="Создать копию"
                                title="Создать копию виджета"
                              >
                                <i className="ti ti-copy" aria-hidden />
                              </button>
                            </>
                          )}
                          <button
                            type="button"
                            className="sigur-dashboard-widget__remove"
                            onClick={() => this.removeWidget(item.i)}
                            onMouseDown={(e) => e.stopPropagation()}
                            onPointerDown={(e) => e.stopPropagation()}
                            aria-label="Удалить виджет"
                          >
                            <i className="ti ti-x" aria-hidden />
                          </button>
                        </>
                      )}
                      {this.renderWidget(item, {
                        statsList,
                        barData,
                        barLabels,
                        donutList,
                        donutTotal,
                        maxBar,
                        BAR_HEIGHT_PX,
                      })}
                    </div>
                  ))}
                </GridLayoutWithWidth>
              </div>
              {this.state.barTooltip && (() => {
                const { x, y, text, left: forcedLeft } = this.state.barTooltip
                const offset = 12
                const pad = 8
                const maxH = 56
                const left = forcedLeft !== undefined ? forcedLeft : x + offset
                const leftClamped = Math.max(pad, left)
                let top = y + offset
                if (top + maxH > window.innerHeight - pad) top = y - offset - maxH
                top = Math.max(pad, Math.min(top, window.innerHeight - maxH - pad))
                return createPortal(
                  <div
                    ref={(el) => this.measureBarTooltip(el)}
                    className="sigur-chart__tooltip"
                    style={{ left: leftClamped + 'px', top: top + 'px' }}
                  >
                    {text}
                  </div>,
                  document.body
                )
              })()}
              {this.renderWidgetConstructorModal()}
              {this.state.showRegisterContractorModal && this.renderRegisterContractorModal()}
            </div>
          </div>

          {roles.has('admin') && (
            <div className="col-12 mt-2">
              <h2 className="sigur-hero__quick-title mb-3">Инструменты</h2>
              <div className="sigur-hero__quick">
                <div className="sigur-hero__grid">
                  <Link to="/skud" className="sigur-hero__card">
                    <i className="ti ti-device-desktop sigur-hero__card-icon" />
                    <div className="sigur-hero__card-body">
                      <span className="sigur-hero__card-title">Удаленный доступ к СКУД</span>
                      <span className="sigur-hero__card-desc">Переход к функционалу удаленного доступа</span>
                    </div>
                    <i className="ti ti-chevron-right sigur-hero__card-arrow" />
                  </Link>
                  <div className="sigur-hero__card">
                    <i className="ti ti-refresh sigur-hero__card-icon" />
                    <div className="sigur-hero__card-body">
                      <span className="sigur-hero__card-title">Интеграция СКУД</span>
                      <span className="sigur-hero__card-desc">
                        {this.state.integrationLastSync
                          ? `Последняя синхронизация: ${formatShortDateTime(this.state.integrationLastSync.at) || this.state.integrationLastSync.at}`
                          : 'Синхронизация режимов с системой СКУД'}
                      </span>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary mt-2"
                        disabled={this.state.integrationSyncing}
                        onClick={(e) => {
                          e.preventDefault()
                          this.syncIntegration()
                        }}
                      >
                        {this.state.integrationSyncing ? 'ЗАПУСК…' : 'СИНХРОНИЗИРОВАТЬ'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }

  renderWidgetConstructorModal() {
    if (!this.state.showWidgetConstructorModal) return null
    return createPortal(
      <div
        ref={this.widgetConstructorModalRef}
        className="modal fade show"
        style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) this.closeWidgetConstructor()
        }}
        role="dialog"
        aria-modal="true"
      >
        <div
          className="modal-dialog sigur-widget-wizard-modal modal-dialog-centered"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="modal-content">
            <WidgetConstructorWizard
              modalRef={this.widgetConstructorModalRef}
              onSave={this.handleWidgetConstructorSave}
              onClose={this.closeWidgetConstructor}
              initialConfig={this.state.editConfig}
              widgetId={this.state.editWidgetId}
            />
          </div>
        </div>
      </div>,
      document.body
    )
  }

  renderTopPanelRightItems() {
    if (this.authorized) {
      return (
        <AuthorizedDropDownMeny
          userTitle={this.user_title}
          onLogout={this.logout}
        />
      )
    }
    return <UnauthorizedDropDownMenu/>
  }
}

export default wrapView(IndexView, baseStateToProps)
